import Card from "common/Boxes/Card";
import { DISPLAY_CURRENCIES } from "common/constants";
import React, { memo } from "react";
import Title from "common/Typography/Title";
import { PrecisionConfig } from "types";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";
import styled from "styled-components";
import Flex from "common/Boxes/Flex";
import { getCoinExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { COIN_CARD_HEIGHT } from "features/dashboard/common/constants";
import TextWithIcon from "common/Text/TextWithIcon";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { ICON_TYPES } from "@shared/common/constants";
import LiveSymbolPrice from "common/LiveComponents/LiveSymbolPrice";
import LivePriceChange from "common/LiveComponents/LivePriceChange";
import { CoinData, LanguageNameSpaces, PrecisionType } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface CoinCardProps {
  coinData: CoinData;
  exchange: string;
  precision?: PrecisionConfig;
}

const CoinCard = ({ coinData, exchange, precision }: CoinCardProps) => {
  const appTheme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { historyPush } = useHistoryWithQuery();
  const isSymbol = coinData.symbol.includes("/");
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);

  const openCoinExtraDetails = () => {
    historyPush(getCoinExtraDetailsRoute(location.pathname, coinData.symbol));
  };

  const coinWithIcon = (
    <TextWithIcon
      text={coinData.symbol}
      coinOrExchangeName={coinData.symbol}
      iconType={ICON_TYPES.COIN}
      style={{
        color: appTheme.colors.secondaryText,
        fontSize: isSymbol
          ? appTheme.typography.fontSizes.S
          : appTheme.typography.fontSizes.M,
        marginBottom: 4,
      }}
    />
  );

  const initialPrice =
    displayCurrency === DISPLAY_CURRENCIES.USD
      ? coinData.usd_price
      : coinData.btc_price;

  const coinPrice = (
    <LiveSymbolPrice
      symbol={coinData.symbol}
      exchange={exchange}
      initialPrice={initialPrice}
      fontSize={appTheme.typography.fontSizes.S}
    />
  );

  const amount = (
    <TextWithPrecisionAndIcon
      text={
        displayCurrency === DISPLAY_CURRENCIES.USD
          ? coinData.total_in_usd
          : coinData.total_in_btc
      }
      precisionConfig={precision}
      precisionType={PrecisionType.BALANCE}
      iconType={ICON_TYPES.DISPLAY_CURRENCY}
    />
  );

  const priceChange =
    (displayCurrency === DISPLAY_CURRENCIES.USD
      ? coinData.graph_data?.change?.[0]
      : coinData.graph_data?.change?.[1]) || 0;

  const coinPriceChange = (
    <LivePriceChange
      symbol={coinData.symbol}
      exchange={exchange}
      initialChange={priceChange}
      size={appTheme.typography.pxFontSizes.S}
      useBackground={true}
    />
  );

  return (
    <StyledCard onClick={openCoinExtraDetails}>
      {coinWithIcon}
      <Flex style={{ width: "100%" }}>
        <Title level={5}>{coinPrice}</Title>
        {coinPriceChange}
      </Flex>
      <HorizontalBorder />
      <Flex style={{ width: "100%" }}>
        <Title type="secondary" level={5}>
          {t("account.coin_total")}
        </Title>
        <Title level={5}>{amount}</Title>
      </Flex>
    </StyledCard>
  );
};

const MemoizedCoinCard = memo(CoinCard);
export default MemoizedCoinCard;

const StyledCard = styled(Card)`
  background: ${(p) => p.theme.colors.dashboardCoinsBgColor};
  border: ${(p) => p.theme.borders.lightest};
  box-shadow: none;
  padding: 8px;
  height: ${COIN_CARD_HEIGHT}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;
const HorizontalBorder = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  width: 100%;
  padding-top: 8px;
  margin-bottom: 8px;
`;
